import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getLeague, getCurrentEvent, getEvent, getFights, getMyPicks, submitPicks, getLeaguePicks, getCommunityStats } from '../services/api';
import Loading from '../components/Loading';
import CountdownTimer from '../components/CountdownTimer';
import FightCard from '../components/FightCard';
import AllPicksView from '../components/AllPicksView';
import getErrorMessage from '../utils/errorHandler';
import { tapMedium, tapSuccess } from '../utils/haptics';
import '../styles/app/picks.css';
import '../styles/app/picks-page.css';

const svgProps = { fill: 'none', stroke: 'currentColor', strokeWidth: 2.2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
const LockIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...svgProps}>
    <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" />
    <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
  </svg>
);
const BackIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" {...svgProps} strokeWidth={2.6}>
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const FlagIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" {...svgProps}>
    <path d="M5 21V4" />
    <path d="M5 4h11l-2 4 2 4H5" />
  </svg>
);
const ClockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" {...svgProps}>
    <circle cx="12" cy="12" r="8.5" />
    <polyline points="12 7.5 12 12 15 14" />
  </svg>
);
const CalendarIcon = () => (
  <svg width="30" height="30" viewBox="0 0 24 24" {...svgProps}>
    <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
    <path d="M3.5 10h17M8 3v4M16 3v4" />
  </svg>
);

function PoolPicks() {
  const { id: poolId } = useParams();
  const [searchParams] = useSearchParams();
  const eventIdParam = searchParams.get('event');
  const userIdParam = searchParams.get('user');
  const navigate = useNavigate();
  const [pool, setPool] = useState(null);
  const [event, setEvent] = useState(null);
  const [fights, setFights] = useState([]);
  const [picks, setPicks] = useState({});
  const [existingPicks, setExistingPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [allUserPicks, setAllUserPicks] = useState(null);
  const [communityStats, setCommunityStats] = useState({});
  const [viewMode, setViewMode] = useState('my'); // 'my', 'all', or 'user'
  const [selectedUser, setSelectedUser] = useState(null);
  const [showLockSheetPrompt, setShowLockSheetPrompt] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchData(() => cancelled);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poolId, eventIdParam, userIdParam]);

  const fetchData = async (isCancelled) => {
    try {
      // If event ID is in URL, fetch that specific event, otherwise get current
      const [poolRes, eventRes] = await Promise.all([
        getLeague(poolId),
        eventIdParam ? getEvent(eventIdParam) : getCurrentEvent()
      ]);
      setPool(poolRes.data);
      setEvent(eventRes.data);

      if (eventRes.data) {
        const isLockedOrCompleted = eventRes.data.status === 'locked' || eventRes.data.status === 'completed';

        // Fetch fights and picks
        const fetchPromises = [
          getFights(eventRes.data.id),
          getMyPicks(poolId, eventRes.data.id).catch(() => ({ data: [] }))
        ];

        // If event is locked/completed, also fetch all users' picks
        if (isLockedOrCompleted) {
          fetchPromises.push(getLeaguePicks(poolId, eventRes.data.id).catch(() => ({ data: { users: [] } })));
        }

        const results = await Promise.all(fetchPromises);
        const [fightsRes, picksRes] = results;
        setFights(fightsRes.data || []);
        setExistingPicks(picksRes.data || []);

        // If locked/completed, set all users' picks
        if (isLockedOrCompleted && results[2]) {
          setAllUserPicks(results[2].data);

          // If user ID param is provided, show that specific user's picks
          if (userIdParam) {
            const targetUser = (results[2].data.users || []).find(u => u.id === parseInt(userIdParam));
            if (targetUser) {
              setSelectedUser(targetUser);
              setViewMode('user');
            } else {
              setViewMode('all');
            }
          } else {
            // Default to "all" view for past/locked events
            setViewMode('all');
          }
        } else {
          setViewMode('my');
          setSelectedUser(null);
        }

        // Initialize picks from existing picks
        const initialPicks = {};
        (picksRes.data || []).forEach(pick => {
          initialPicks[pick.fight_id] = {
            winner: pick.picked_fighter,
            method: pick.picked_method
          };
        });
        setPicks(initialPicks);

        // Fetch global community pick stats (non-blocking)
        getCommunityStats(eventRes.data.id)
          .then(statsRes => {
            if (isCancelled && isCancelled()) return;
            const statsMap = {};
            (statsRes.data || []).forEach(s => { statsMap[s.fight_id] = s; });
            setCommunityStats(statsMap);
          })
          .catch(err => console.error('Community stats fetch failed:', err));
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePick = (fightId, winner, method = null) => {
    setPicks(prev => ({
      ...prev,
      [fightId]: { winner, method: method || prev[fightId]?.method }
    }));
  };

  const handleMethodPick = (fightId, method) => {
    setPicks(prev => ({
      ...prev,
      [fightId]: { ...prev[fightId], method }
    }));
  };

  const handleSubmit = async () => {
    const eligibleFights = fights.filter(fight => {
      if (fight.is_cancelled) return false;
      const inclusion = pool?.fight_inclusion || (pool?.include_all_fights === 0 ? 'main' : 'all');
      if (inclusion === 'main') return fight.card_type === 'main';
      if (inclusion === 'main_prelims') return fight.card_type === 'main' || fight.card_type === 'prelim';
      return true;
    });

    // Get picks that have a winner selected (allow partial submissions)
    const picksWithWinner = Object.entries(picks).filter(([, pick]) => pick?.winner);

    if (picksWithWinner.length === 0) {
      setMessage({ type: 'error', text: 'Please pick at least one winner before saving' });
      return;
    }

    // Validate method picks only for fights that have been picked
    // and where method is required for that card type
    const methodReq = pool?.method_required_for || 'main';
    for (const [fightId, pick] of picksWithWinner) {
      const fight = eligibleFights.find(f => f.id === parseInt(fightId));
      if (!fight) continue;

      const needsMethod = (methodReq === 'main' && fight.card_type === 'main') ||
                          (methodReq === 'main_prelims' && (fight.card_type === 'main' || fight.card_type === 'prelim')) ||
                          (methodReq === 'all');

      if (needsMethod && !pick.method) {
        setMessage({ type: 'error', text: `Please select a method of victory for ${fight.fighter1_name} vs ${fight.fighter2_name}` });
        return;
      }
    }

    setSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      const picksData = picksWithWinner
        .filter(([fightId]) => eligibleFights.some(fight => fight.id === parseInt(fightId)))
        .map(([fightId, pick]) => ({
          fight_id: parseInt(fightId),
          picked_fighter: pick.winner,
          picked_method: pick.method
        }));

      await submitPicks({
        league_id: parseInt(poolId),
        event_id: event.id,
        picks: picksData
      });

      const remaining = eligibleFights.length - picksData.length;
      if (remaining > 0) {
        tapMedium();
        setMessage({ type: 'success', text: `Saved ${picksData.length} picks! ${remaining} fights still need picks.` });
      } else {
        tapSuccess();
        setMessage({ type: 'success', text: 'All picks locked in!' });
        setShowLockSheetPrompt(true);
      }
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error) });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Loading />;

  if (!event) {
    return (
      <div className="container pk-page">
        <div className="pnp-empty pk-empty">
          <div className="pnp-empty-icon"><CalendarIcon /></div>
          <h2 className="pnp-empty-title">No Active Event</h2>
          <p>Check back when an event is scheduled.</p>
          <div className="pnp-empty-actions">
            <Link to={`/pool/${poolId}`} className="btn btn-primary">
              Back to Pool
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const pickedCount = Object.keys(picks).filter(id => picks[id]?.winner).length;
  const isLocked = event.status === 'locked' || event.status === 'completed';
  const isOpen = event.status === 'open';
  const picksDisabled = isLocked || !isOpen;

  // Get allowed card types based on pool settings (supports both legacy and new format)
  const getAllowedCardTypes = () => {
    // Check new field first, then fall back to legacy
    const inclusion = pool?.fight_inclusion || (pool?.include_all_fights === 0 ? 'main' : 'all');
    switch (inclusion) {
      case 'main':
        return ['main'];
      case 'main_prelims':
        return ['main', 'prelim'];
      case 'all':
      default:
        return ['main', 'prelim', 'early'];
    }
  };

  // Check if method is required for a given card type
  const isMethodRequiredForCardType = (cardType) => {
    const methodReq = pool?.method_required_for || 'main';
    switch (methodReq) {
      case 'none':
        return false;
      case 'main':
        return cardType === 'main';
      case 'main_prelims':
        return cardType === 'main' || cardType === 'prelim';
      case 'all':
        return true;
      default:
        return cardType === 'main';
    }
  };

  const allowedCardTypes = getAllowedCardTypes();
  const activeFights = fights.filter(fight => {
    if (fight.is_cancelled) return false;
    return allowedCardTypes.includes(fight.card_type);
  });
  const mainCardFights = activeFights.filter(f => f.card_type === 'main').sort((a, b) => (a.fight_order ?? 0) - (b.fight_order ?? 0));
  const prelimFights = activeFights.filter(f => f.card_type === 'prelim').sort((a, b) => (a.fight_order ?? 0) - (b.fight_order ?? 0));
  const earlyFights = activeFights.filter(f => f.card_type === 'early').sort((a, b) => (a.fight_order ?? 0) - (b.fight_order ?? 0));

  const showPrelims = allowedCardTypes.includes('prelim');
  const showEarlyPrelims = allowedCardTypes.includes('early');

  const progressPct = activeFights.length > 0 ? Math.min(100, (pickedCount / activeFights.length) * 100) : 0;
  const allPicked = activeFights.length > 0 && pickedCount === activeFights.length;
  const showDock = !picksDisabled && viewMode === 'my';

  const renderProgress = () => (
    <div className="pk-dock-progress">
      <div className="pk-dock-top">
        <span className="pk-dock-label">Progress</span>
        <span className="pk-dock-count">{pickedCount} / {activeFights.length} picks</span>
      </div>
      <div className="pk-dock-bar" aria-hidden="true">
        <div style={{ width: `${progressPct}%` }} />
      </div>
    </div>
  );

  return (
    <div className={`container pk-page ${showDock ? 'has-dock' : ''}`}>
      <Link to={`/pool/${poolId}`} className="pk-back">
        <BackIcon />
        <span>Back to {pool?.name}</span>
      </Link>

      <header className="pk-hero">
        <span className="pnp-claw pk-hero-claw" aria-hidden="true" />
        <div className="pk-hero-main">
          <p className="pnp-eyebrow">{pool?.name}</p>
          <h1 className="pnp-title pk-hero-title">{event.name}</h1>
          <p className="pk-hero-date">
            {new Date(event.date).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </p>
        </div>
        {isOpen && (
          <div className="pk-hero-clock">
            <CountdownTimer targetDate={event.date} label="Picks close in" />
          </div>
        )}
      </header>

      {isLocked && (
        <div className={`pk-banner ${event.status === 'completed' ? 'is-completed' : 'is-locked'}`}>
          <span className="pk-banner-icon">
            {event.status === 'completed' ? <FlagIcon /> : <LockIcon size={18} />}
          </span>
          <p>
            {event.status === 'completed' ? 'Event Completed - View Results' : 'Picks are locked for this event'}
          </p>
        </div>
      )}

      {/* View Toggle - show when event is locked/completed */}
      {isLocked && allUserPicks && (
        <div className="pk-toolbar">
          <div className="pnp-segmented pk-view-toggle" role="group" aria-label="Picks view">
            <button
              type="button"
              className={viewMode === 'all' ? 'active' : ''}
              onClick={() => { setViewMode('all'); setSelectedUser(null); }}
            >
              All Picks
            </button>
            <button
              type="button"
              className={viewMode === 'my' ? 'active' : ''}
              onClick={() => { setViewMode('my'); setSelectedUser(null); }}
            >
              My Picks
            </button>
          </div>
          {selectedUser && viewMode === 'user' && (
            <span className="pnp-chip pnp-chip-red pk-viewing">
              Viewing: {selectedUser.username}
            </span>
          )}
        </div>
      )}
      {!isLocked && !isOpen && (
        <div className="pk-banner is-soon">
          <span className="pk-banner-icon"><ClockIcon /></span>
          <p>
            Picks will open soon. Check back later!
          </p>
        </div>
      )}

      {message.text && (
        <div className={`message ${message.type === 'error' ? 'message-error' : 'message-success'} pk-message`}>
          {message.text}
        </div>
      )}

      {/* Progress card when picks are closed - only show when viewing own picks */}
      {viewMode === 'my' && picksDisabled && (
        <div className="pk-dock is-static">
          {renderProgress()}
        </div>
      )}

      <div className="pk-scoring">
        <p>
          <strong>Scoring:</strong> 5 points for correct winner
          {pool?.method_required_for !== 'none' && ` + ${pool?.method_bonus_points || 3} point method bonus`}
          {pool?.championship_multiplier > 1 && (
            <span> · Title fights x{pool.championship_multiplier}</span>
          )}
        </p>
      </div>

      {/* Build picks map for selected user when viewing their picks */}
      {(() => {
        // Build picks map for selected user
        const viewingUserPicks = {};
        if (viewMode === 'user' && selectedUser) {
          (selectedUser.picks || []).forEach(pick => {
            viewingUserPicks[pick.fight_id] = {
              winner: pick.picked_fighter,
              method: pick.picked_method
            };
          });
        }
        const displayPicks = viewMode === 'user' && selectedUser ? viewingUserPicks : picks;

        // Calculate stats for selected user
        const selectedUserStats = selectedUser ? {
          correctPicks: (selectedUser.picks || []).filter(p => p.is_correct).length,
          totalPicks: (selectedUser.picks || []).filter(p => !p.is_draw).length,
          totalPoints: (selectedUser.picks || []).reduce((sum, p) => sum + (p.points_earned || 0), 0),
          methodBonus: (selectedUser.picks || []).reduce((sum, p) => sum + (p.method_bonus || 0), 0)
        } : null;

        const sectionCount = (list) => (
          <span className="pk-section-count">
            {list.filter(f => displayPicks[f.id]?.winner).length}<span>/{list.length}</span>
          </span>
        );

        return viewMode === 'all' && allUserPicks ? (
          <AllPicksView
            users={allUserPicks.users || []}
            fights={fights}
            pool={pool}
          />
        ) : (
        <>
          {/* User Stats Card - show when viewing another user's picks */}
          {viewMode === 'user' && selectedUser && selectedUserStats && (
            <div className="card pnp-glass-red pk-user-card">
              <div className="pk-user-card-id">
                <h3>{selectedUser.username}'s Picks</h3>
                <p>
                  {selectedUserStats.correctPicks}/{selectedUserStats.totalPicks} correct picks
                </p>
              </div>
              <div className="pk-user-card-stats">
                <div className="pk-user-stat">
                  <div className="pk-user-stat-label">Method Bonus</div>
                  <div className="pk-user-stat-value is-green">+{selectedUserStats.methodBonus}</div>
                </div>
                <div className="pk-user-stat">
                  <div className="pk-user-stat-label">Total Points</div>
                  <div className="pk-user-stat-value is-big">{selectedUserStats.totalPoints}</div>
                </div>
              </div>
            </div>
          )}

          <section className="pk-section">
            <div className="pk-section-head">
              <h2 className="pk-section-title">
                Main Card
                <span className="pk-pts">
                  5 pts{isMethodRequiredForCardType('main') ? ` + ${pool?.method_bonus_points || 3} pt method bonus` : ''}
                </span>
              </h2>
              {mainCardFights.length > 0 && sectionCount(mainCardFights)}
            </div>
            {mainCardFights.length === 0 ? (
              <div className="pk-none">No main card fights available.</div>
            ) : (
              <div className="pk-grid">
                {mainCardFights.map((fight, idx) => (
                  <FightCard
                    key={fight.id}
                    fight={fight}
                    pick={displayPicks[fight.id]}
                    onPick={(winner) => handlePick(fight.id, winner)}
                    onMethodPick={(method) => handleMethodPick(fight.id, method)}
                    showMethod={isMethodRequiredForCardType(fight.card_type)}
                    disabled={picksDisabled || viewMode === 'user'}
                    isTitle={fight.is_title_fight}
                    communityStats={communityStats[fight.id]}
                    championshipMultiplier={pool?.championship_multiplier}
                    featured={!!fight.is_main_event || idx === 0}
                  />
                ))}
              </div>
            )}
          </section>

          {showPrelims && (
            <section className="pk-section">
              <div className="pk-section-head">
                <h2 className="pk-section-title">
                  Prelims
                  <span className="pk-pts">
                    3 pts{isMethodRequiredForCardType('prelim') ? ` + ${pool?.method_bonus_points || 3} pt method bonus` : ''}
                  </span>
                </h2>
                {prelimFights.length > 0 && sectionCount(prelimFights)}
              </div>
              {prelimFights.length === 0 ? (
                <div className="pk-none">No prelim fights available.</div>
              ) : (
                <div className="pk-grid">
                  {prelimFights.map(fight => (
                    <FightCard
                      key={fight.id}
                      fight={fight}
                      pick={displayPicks[fight.id]}
                      onPick={(winner) => handlePick(fight.id, winner)}
                      onMethodPick={(method) => handleMethodPick(fight.id, method)}
                      showMethod={isMethodRequiredForCardType('prelim')}
                      disabled={picksDisabled || viewMode === 'user'}
                      isTitle={fight.is_title_fight}
                      communityStats={communityStats[fight.id]}
                    />
                  ))}
                </div>
              )}
            </section>
          )}

          {showEarlyPrelims && (
            <section className="pk-section">
              <div className="pk-section-head">
                <h2 className="pk-section-title">
                  Early Prelims
                  <span className="pk-pts">
                    1 pt{isMethodRequiredForCardType('early') ? ` + ${pool?.method_bonus_points || 3} pt method bonus` : ''}
                  </span>
                </h2>
                {earlyFights.length > 0 && sectionCount(earlyFights)}
              </div>
              {earlyFights.length === 0 ? (
                <div className="pk-none">No early prelim fights available.</div>
              ) : (
                <div className="pk-grid">
                  {earlyFights.map(fight => (
                    <FightCard
                      key={fight.id}
                      fight={fight}
                      pick={displayPicks[fight.id]}
                      onPick={(winner) => handlePick(fight.id, winner)}
                      onMethodPick={(method) => handleMethodPick(fight.id, method)}
                      showMethod={isMethodRequiredForCardType('early')}
                      disabled={picksDisabled || viewMode === 'user'}
                      isTitle={fight.is_title_fight}
                      communityStats={communityStats[fight.id]}
                      championshipMultiplier={pool?.championship_multiplier}
                    />
                  ))}
                </div>
              )}
            </section>
          )}
        </>
        );
      })()}

      {/* Sticky save bar - show when picks are open and viewing own picks */}
      {showDock && (
        <div className={`pk-dock ${allPicked ? 'is-complete' : ''}`}>
          {message.text && (
            <div className={`pk-dock-msg ${message.type === 'error' ? 'is-error' : 'is-success'}`} role="status">
              {message.text}
            </div>
          )}
          <div className="pk-dock-info">
            {renderProgress()}
            <p className="pk-dock-hint">
              {pickedCount === activeFights.length
                ? 'All picks complete!'
                : `${pickedCount}/${activeFights.length} picks - save anytime, finish before lock`}
            </p>
          </div>
          <button
            type="button"
            className="btn btn-primary pk-dock-btn"
            onClick={handleSubmit}
            disabled={submitting || pickedCount === 0}
          >
            {submitting ? 'Saving...' : existingPicks.length > 0 ? 'Update Picks' : 'Save Picks'}
          </button>
        </div>
      )}

      {/* Lock Sheet Prompt - Show after all picks submitted */}
      {showLockSheetPrompt && (
        <div className="pk-modal" role="dialog" aria-modal="true" aria-labelledby="pk-modal-title">
          <div className="pk-modal-scrim" onClick={() => { setShowLockSheetPrompt(false); navigate(`/pool/${poolId}`); }} />
          <div className="modal-card pk-modal-card">
            <div className="pk-modal-icon"><LockIcon size={30} /></div>
            <h3 id="pk-modal-title" className="pk-modal-title">Picks Locked In!</h3>
            <p className="pk-modal-sub">
              Share your Lock Sheet on Instagram
            </p>
            <button
              type="button"
              className="btn btn-primary btn-lg btn-block"
              onClick={() => {
                setShowLockSheetPrompt(false);
                navigate(`/lock-sheet?event=${event.id}&pool=${poolId}`);
              }}
            >
              View Lock Sheet
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-block pk-modal-skip"
              onClick={() => { setShowLockSheetPrompt(false); navigate(`/pool/${poolId}`); }}
            >
              Skip
            </button>
          </div>
        </div>
      )}

      {/* View Lock Sheet - shown when picks are submitted */}
      {existingPicks.length > 0 && (
        <div className="pk-locksheet">
          <span className="pk-locksheet-icon"><LockIcon size={20} /></span>
          <div className="pk-locksheet-copy">
            <p>
              Save your Lock Sheet to camera roll and share on Instagram
            </p>
          </div>
          <button
            type="button"
            className="btn btn-secondary pk-locksheet-btn"
            onClick={() => navigate(`/lock-sheet?event=${event?.id}&pool=${poolId}`)}
          >
            View Lock Sheet
          </button>
        </div>
      )}
    </div>
  );
}

export default PoolPicks;
